// Supabase client wrapper + result envelope + complete-set pagination.
//
// ── Vendor import approach (documented decision) ───────────────────────────
// vendor/supabase-js/supabase.js is the pinned browser build. It is an IIFE
// that assigns a GLOBAL `supabase` variable — it is not a CJS/ESM module, so
// `import … from '../vendor/…'` yields no exports under esbuild. We therefore
// load it as a CLASSIC <script> tag BEFORE app.js (build.mjs copies it to
// dist/vendor/supabase.js; CSP script-src 'self' allows it) and this module
// reads `globalThis.supabase.createClient`. That keeps the vendored bytes
// byte-identical to the pinned upstream build (auditable via the sha256 in
// vendor/README.md) instead of rebundling them.

import { PT_CHUNK } from './contract.js';

// ─── Supabase project config (legacy v20 lines 898-899) ────────────────────
// The values themselves moved to ./creds.js so the DEMO build can be given a
// stub module in their place at bundle time (R-034 — see the long note there:
// the demo bundle must not merely refuse to use the live coordinates, it must
// not CONTAIN them). Re-exported here because every existing caller imports
// them from this module.
export { SUPABASE_URL, SUPABASE_ANON_KEY, IS_LIVE_CREDS } from './creds.js';

// ─── {ok,data,error,code} envelope ─────────────────────────────────────────
// Every db-layer function returns this envelope instead of throwing — callers
// branch on .ok and hand .error to errors.netErr() for user-facing copy.

/**
 * @template T
 * @param {T} data
 * @returns {{ok: true, data: T, error: null, code: null}}
 */
export function ok(data) {
  return { ok: true, data, error: null, code: null };
}

/**
 * @param {any} error driver error (or Error) — kept for logging, never shown raw
 * @param {string|null} [code] machine-readable code (defaults to error.code)
 * @returns {{ok: false, data: null, error: any, code: string|null}}
 */
export function fail(error, code) {
  return { ok: false, data: null, error, code: code ?? (error && error.code) ?? null };
}

// ─── Client construction ───────────────────────────────────────────────────
/** @type {any} */
let _client = null;

/**
 * Create (once) and return the Supabase client from the vendored global.
 *
 * DEV-ONLY injection seam: a pre-built client may be passed as the SOLE
 * argument (an object, not a URL string) — the dev harness passes its
 * mock-Supabase client this way so the REAL src modules run headless without a
 * network. This is dependency injection, NOT a production backdoor: prod
 * main.js always calls initClient(url, anonKey) with two strings, so this
 * branch is unreachable in the shipped bundle. The seam constructs nothing and
 * reaches no network — it only memoizes a client the caller already built.
 * @param {string|any} urlOrClient Supabase project URL, OR a pre-built client (dev only)
 * @param {string} [anonKey] public anon key (omitted when injecting a client)
 * @returns {any} the supabase-js client
 */
export function initClient(urlOrClient, anonKey) {
  if (_client) return _client;
  if (urlOrClient && typeof urlOrClient === 'object') {
    _client = urlOrClient; // dev injection — see the seam note above
    return _client;
  }
  const lib = /** @type {any} */ (globalThis).supabase;
  if (!lib || typeof lib.createClient !== 'function') {
    throw new Error('supabase vendor script not loaded — check the <script src="vendor/supabase.js"> tag order');
  }
  _client = lib.createClient(urlOrClient, anonKey);
  return _client;
}

/**
 * The initialized client. Throws if initClient() has not run — no module may
 * construct its own client or fall back to a hidden default.
 * @returns {any}
 */
export function getClient() {
  if (!_client) throw new Error('db client not initialized — call initClient(url, anonKey) during boot');
  return _client;
}

/** Test-only: reset the memoized client. @returns {void} */
export function _resetClient() {
  _client = null;
}

// ─── Programme filter (legacy applyProgrammeFilter — ADR-002, implemented once) ─
/**
 * Narrow a query to the active programme, matching the VALUE TYPE to the
 * column (F-PROG-03 safety): a `*_code`/`programme_code` column takes the TEXT
 * code, everything else takes the UUID programme_id. Passing the wrong type
 * silently empties the view under PostgREST, so this single helper is the only
 * sanctioned way to programme-scope a query. Exact legacy port.
 * @param {any} query a supabase query builder (returned unchanged when 'all')
 * @param {string} column the column to filter on (default programme_id)
 * @param {string} active 'all' or a programme code (SLICES.FILTERS.programme)
 * @param {Record<string, any>} byCode programme catalogue keyed by code
 * @returns {any} the (possibly narrowed) builder
 */
export function applyProgrammeFilter(query, column, active, byCode) {
  const col = column || 'programme_id';
  if (!active || active === 'all') return query;
  const p = byCode && byCode[active];
  if (!p) return query;
  const isCodeColumn = col === 'programme_code' || /_code$/.test(col);
  const value = isCodeColumn ? p.code || active : p.programme_id;
  return query.eq(col, value);
}

// ─── Complete-set loader (legacy _loadAllCasesInner chunk loop) ────────────
/**
 * Fetch the COMPLETE matching set by looping .range() in chunk-sized pages
 * until every row is retrieved (works past the PostgREST 1000-row cap).
 *
 * `makeQuery` must return a FRESH query builder per call (a supabase builder
 * is consumed by awaiting it, so the loop re-creates it for every page —
 * exactly like the legacy loop re-invoked `_sb.from(…).select(…)` each pass).
 * The builder must resolve to `{data, count, error}`; pass `{count:'exact'}`
 * in the select for a true total.
 *
 * Loop-exit semantics (ported verbatim):
 *  - a short/empty chunk ends the loop;
 *  - reaching the known `count` total ends the loop;
 *  - any page error aborts and returns fail(error);
 *  - `opts.shouldAbort()` returning true after any page abandons the loop
 *    (legacy's in-loop epoch check, v20:1722) — a superseded load stops at the
 *    next page boundary instead of paging a whole >1000-row set it will throw
 *    away. The caller still re-checks before committing.
 *
 * @param {() => any} makeQuery returns a fresh builder (order applied, no range)
 * @param {{chunk?: number, shouldAbort?: () => boolean}} [opts]
 * @returns {Promise<any>}
 */
export async function fetchAll(makeQuery, opts) {
  const chunk = (opts && opts.chunk) || PT_CHUNK;
  const shouldAbort = opts && opts.shouldAbort;
  const all = [];
  /** @type {number|null} */
  let total = null;
  let from = 0;
  for (;;) {
    const { data, count, error } = await makeQuery().range(from, from + chunk - 1);
    if (shouldAbort && shouldAbort()) {
      return { ok: false, data: null, error: null, code: 'STALE', stale: true };
    }
    if (error) return fail(error);
    const batch = data || [];
    all.push(...batch);
    if (typeof count === 'number') total = count;
    if (batch.length < chunk) break; // short/empty chunk ends it
    if (total !== null && all.length >= total) break; // reached the known total
    from += chunk;
  }
  return Object.assign(ok(all), { total: total !== null ? total : all.length });
}
